import { styled, Box, Button, InputBase } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { format } from "date-fns";

import { Typography } from "components/Typography";
import { ExportIcon, SearchIcon } from "components/Icons";
import { BusinessOptions } from "./components/BusinessOptions";
import { AddBusiness } from "./components/AddBusiness";
import { ApiKey } from "./components/ApiKey/ApiKey";
import { useGetBusinessesQuery } from "api/business";
import { DataGrid } from "components/DataGrid";
import { exportCSV } from "utils/exportCsv";

const Wrapper = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const CsvButton = styled(Button)(() => ({
  borderColor: "#EFF1F4",
  color: "black",
  height: "40px",
  textTransform: "capitalize",
}));

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  border: "1.5px solid #EFF1F4",
  borderRadius: "8px",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

const generateTableColumns = ({ handleClickRowOption }) => [
  { field: "name", headerName: "BUSINESS NAME", flex: 1 },
  { field: "category", headerName: "BUSINESS CATEGORY", flex: 1 },
  { field: "contactEmail", headerName: "EMAIL ADDRESS", flex: 1 },
  {
    field: "createdOn",
    headerName: "DATE & TIME",
    valueFormatter: ({ value }) =>
      format(new Date(Number(value) * 1000), "dd/MM/yyyy hh:mm a"),
    flex: 1,
  },

  {
    field: "",
    headerName: "",
    align: "center",
    renderCell: ({ row }) => (
      <BusinessOptions
        clicker={(option) => handleClickRowOption(option, row)}
      />
    ),
  },
];

export const Businesses = () => {
  const navigate = useNavigate();
  const { data = [], isLoading } = useGetBusinessesQuery();
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleClickRowOption = (option, row) => {
    if (option === "View business details") {
      navigate(`/businesses/${row.businessId}/transactions`);
    } else if (option === "Generate API key") {
      setSelectedRow(row);
    }
  };
  const handleExportCSV = () => {
    if (filteredData.length === 0) return;

    const formatted = filteredData.map(
      ({ name, category, contactEmail, createdOn }) => ({
        Name: name,
        Category: category,
        "Contact Email": contactEmail,
        Date: format(new Date(createdOn * 1000), "dd/MM/yyyy hh:mm a"),
      })
    );

    exportCSV(formatted, "businesses.csv");
  };

  const filteredData = useMemo(() => {
    return data.filter(({ name, category, contactEmail }) => {
      const term = searchTerm.toLowerCase();
      return (
        name?.toLowerCase().includes(term) ||
        category?.toLowerCase().includes(term) ||
        contactEmail?.toLowerCase().includes(term)
      );
    });
  }, [data, searchTerm]);

  return (
    <Box>
      <Wrapper sx={{ justifyContent: "space-between", marginBottom: 3 }}>
        <Wrapper>
          <Typography variant="dashboardHeading">Businesses</Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              inputProps={{ "aria-label": "search" }}
            />
          </Search>
        </Wrapper>

        <Wrapper sx={{ columnGap: "8px" }}>
          <AddBusiness />
          <CsvButton variant="outlined" onClick={handleExportCSV}>
            <Wrapper>
              <ExportIcon />
              <div style={{ marginTop: 5 }}>Export csv</div>
            </Wrapper>
          </CsvButton>
        </Wrapper>
      </Wrapper>

      <DataGrid
        rows={filteredData}
        loading={isLoading}
        columns={generateTableColumns({ handleClickRowOption })}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[9]}
        disableColumnMenu
        disableRowSelectionOnClick
      />

      <ApiKey
        open={Boolean(selectedRow)}
        handleClose={() => setSelectedRow(null)}
        businessId={selectedRow?.businessId}
      />
    </Box>
  );
};
